/**
 * Phase 1.5 视频墙：
 * - 本地镜像 + 每个远端 peer 的 <video>；
 * - 显示双方姓名/阵营/辩位；
 * - 帧率选择（15/24/30）与摄像头开关；
 * - 无视频轨时保留占位卡（语音仍可用），不制造“黑块”。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Participant } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { useSettings, updateSettings, type VideoFps } from '../../state/settings.ts';

function seatText(participant?: Participant | null): string {
  if (!participant) return '';
  const side = participant.side === 'AFF' ? '正方' : participant.side === 'NEG' ? '反方' : '观察';
  return participant.role === 'TEACHER'
    ? '教师'
    : `${side}${participant.seat ? ` · ${participant.seat}辩` : ''}`;
}

function hasLiveVideo(stream: MediaStream | null): boolean {
  if (!stream) return false;
  return stream.getVideoTracks().some((track) => track.readyState === 'live' && track.enabled);
}

function VideoSurface({
  stream,
  muted,
  mirrored,
}: {
  stream: MediaStream | null;
  muted: boolean;
  mirrored: boolean;
}) {
  const attach = useCallback(
    (element: HTMLVideoElement | null) => {
      if (!element) return;
      if (element.srcObject !== stream) element.srcObject = stream;
      element.muted = muted;
      const play = element.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    },
    [stream, muted],
  );
  return (
    <video
      ref={attach}
      autoPlay
      playsInline
      muted={muted}
      className="h-full w-full object-cover"
      style={{ transform: mirrored ? 'scaleX(-1)' : undefined }}
    />
  );
}

function VideoTile({
  id,
  name,
  meta,
  stream,
  isSelf,
  mirrored,
  cameraEnabled,
}: {
  id: string;
  name: string;
  meta: string;
  stream: MediaStream | null;
  isSelf: boolean;
  mirrored: boolean;
  cameraEnabled: boolean;
}) {
  const active = cameraEnabled && hasLiveVideo(stream);
  return (
    <article
      data-testid={`video-tile-${id}`}
      className="relative min-w-0 overflow-hidden rounded-lg bg-black/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
    >
      <div className="relative aspect-video w-full bg-[radial-gradient(circle_at_50%_35%,rgba(255,255,255,0.07),rgba(4,5,7,0.9))]">
        {active ? (
          <VideoSurface stream={stream} muted mirrored={mirrored} />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="mx-auto grid h-9 w-9 place-items-center rounded-full bg-white/[0.06] text-[13px] text-slate-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]">
                {name.slice(0, 1) || '?'}
              </div>
              <p className="mt-1.5 text-[9px] text-slate-500">{stream ? '仅语音' : '等待视频'}</p>
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-5">
          <div className="min-w-0">
            <p className="truncate text-[10.5px] font-medium text-slate-100" data-testid="video-name">
              {name}
              {isSelf ? <span className="ml-1 text-[8.5px] text-slate-400">你</span> : null}
            </p>
            <p className="truncate text-[8.5px] text-slate-400">{meta || '观察'}</p>
          </div>
          <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] ${active ? 'bg-emerald-300/10 text-emerald-200' : 'bg-white/[0.06] text-slate-400'}`}>
            {active ? 'LIVE' : 'AUDIO'}
          </span>
        </div>
      </div>
    </article>
  );
}

export function VideoWall({ prefix = 'hud', compact = false }: { prefix?: 'hud' | 'coach'; compact?: boolean }) {
  const { state, actions } = useArena();
  const settings = useSettings();
  const [streamTick, setStreamTick] = useState(0);
  const localStream = actions.getLocalStream();
  const relay = useRef<number | null>(null);

  // MediaStream 对象可能在远端 track 到达后原地变化，用低频 tick 保证重渲染。
  useEffect(() => {
    relay.current = window.setInterval(() => setStreamTick((value) => value + 1), 1200);
    return () => {
      if (relay.current) window.clearInterval(relay.current);
    };
  }, []);

  const remotes = actions.getRemoteStreams();
  const self = state.self;
  const peers = state.participants.filter((participant) => participant.id !== self?.id);
  const tiles = [
    ...(self
      ? [
          {
            id: self.id,
            name: `${self.name}${self.role === 'TEACHER' ? '（教师）' : ''}`,
            meta: seatText(self),
            stream: localStream,
            isSelf: true,
          },
        ]
      : []),
    ...peers.map((participant) => ({
      id: participant.id,
      name: participant.name,
      meta: seatText(participant),
      stream: remotes.find((item) => item.peerId === participant.id)?.stream ?? null,
      isSelf: false,
    })),
  ].slice(0, compact ? 4 : 6);

  return (
    <section
      data-testid={`${prefix}-video-wall`}
      data-stream-tick={streamTick}
      className="lens p-2.5"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 px-0.5 pb-2">
        <div className="flex items-center gap-2">
          <span className="micro-label">VIDEO WALL</span>
          <span className="pill">{tiles.length} 路</span>
          <span className="text-[9px] text-slate-500">姓名 / 辩位已叠加</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <label className="flex items-center gap-1 text-[9px] text-slate-500">
            FPS
            <select
              data-testid={`${prefix}-video-fps`}
              value={settings.videoFps}
              onChange={(event) => updateSettings({ videoFps: Number(event.target.value) as VideoFps })}
              className="rounded border border-white/10 bg-black/30 px-1.5 py-1 text-[9px] text-slate-200"
            >
              {[15, 24, 30].map((fps) => (
                <option key={fps} value={fps}>{fps}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid={`${prefix}-video-camera`}
            className={`transport-btn !min-h-7 !py-1 ${state.peering.cameraEnabled ? 'transport-btn--success' : ''}`}
            onClick={() => void actions.setCameraEnabled(!state.peering.cameraEnabled)}
          >
            摄像头{state.peering.cameraEnabled ? '开' : '关'}
          </button>
        </div>
      </header>
      <div className={`grid gap-1.5 ${compact ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4'}`}>
        {tiles.length === 0 ? (
          <p className="col-span-full px-1 py-4 text-center text-[10px] text-slate-500">
            等待参与者加入；视频会在这里按姓名/辩位排列。
          </p>
        ) : (
          tiles.map((tile) => (
            <VideoTile
              key={tile.id}
              id={tile.id}
              name={tile.name}
              meta={tile.meta}
              stream={tile.stream}
              isSelf={tile.isSelf}
              mirrored={settings.mirrorLocalVideo && tile.isSelf}
              cameraEnabled={tile.isSelf ? state.peering.cameraEnabled : true}
            />
          ))
        )}
      </div>
      <p className="mt-1.5 px-0.5 text-[8.5px] text-slate-600">
        帧率用于下一次启用/重连采集；切换摄像头会替换轨道，不重开房间。
      </p>
    </section>
  );
}
